import { BoardMeetingCommentWithCreatedBy } from "@boark/shared/types/board-meeting-comment-with-created-by";
import { BoardMeetingContentType } from "@boark/shared/types/board-meeting-content";
import { BoardMeetingSectionWithContents } from "@boark/shared/types/board-meeting-section-with-contents";
import { BoardMeetingWithDetails } from "@boark/shared/types/board-meeting-with-details";
import { canEditBoardMeetingAgenda } from "@boark/shared/utils/board-meeting";
import {
  faArrowDown,
  faArrowUp,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

import { useBoardMeetingMutation } from "../queries/board-meeting";
import {
  createBoardMeetingContent,
  createBoardMeetingSection,
  deleteBoardMeetingSection,
  reorderBoardMeetingContents,
  reorderBoardMeetingSections,
  updateBoardMeetingSection,
} from "../services/board-meeting";
import { useLocale } from "../stores/locale";
import { BoardMeetingContentItem } from "./board-meeting-content-item";
import { Button } from "./button";
import { Confirm } from "./confirm";
import { IconButton } from "./icon-button";
import { Input } from "./input";

// Moves the item at index one step in direction and returns the new id order
function moveId(ids: string[], index: number, direction: -1 | 1): string[] {
  const target = index + direction;
  if (target < 0 || target >= ids.length) {
    return ids;
  }
  const result = [...ids];
  [result[index], result[target]] = [result[target]!, result[index]!];
  return result;
}

export function BoardMeetingAgenda({
  meeting,
  authUserId,
  comments,
}: {
  meeting: BoardMeetingWithDetails;
  authUserId?: string;
  comments: BoardMeetingCommentWithCreatedBy[];
}) {
  const { locale: t } = useLocale();
  const [newSectionName, setNewSectionName] = useState("");
  const editable = canEditBoardMeetingAgenda(meeting, authUserId);

  const createSection = useBoardMeetingMutation(meeting.id, (name: string) =>
    createBoardMeetingSection(meeting.id, { name }),
  );
  const reorderSections = useBoardMeetingMutation(meeting.id, (ids: string[]) =>
    reorderBoardMeetingSections(meeting.id, ids),
  );

  const sectionIds = meeting.sections.map((section) => section.id);

  return (
    <div className="flex flex-col gap-6">
      {meeting.sections.length === 0 && (
        <p className="text-gray-500 m-0">{t.boardMeeting.sectionsEmpty}</p>
      )}
      {meeting.sections.map((section, index) => (
        <AgendaSection
          key={section.id}
          authUserId={authUserId}
          comments={comments}
          editable={editable}
          isFirst={index === 0}
          isLast={index === meeting.sections.length - 1}
          meeting={meeting}
          number={index + 1}
          section={section}
          onMove={(direction) =>
            reorderSections.mutate(moveId(sectionIds, index, direction))
          }
        />
      ))}
      {editable && (
        <form
          className="flex flex-wrap items-end gap-2 border-t border-gray-200 pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            const name = newSectionName.trim();
            if (name === "") {
              return;
            }
            void createSection.mutateAsync(name).then(() => {
              setNewSectionName("");
            });
          }}
        >
          <Input
            className="flex-1 min-w-48"
            label={t.boardMeeting.sectionName}
            type="text"
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
          />
          <Button
            disabled={newSectionName.trim() === ""}
            loading={createSection.isPending}
            type="submit"
          >
            {t.boardMeeting.sectionAdd}
          </Button>
        </form>
      )}
    </div>
  );
}

function AgendaSection({
  meeting,
  section,
  number,
  editable,
  authUserId,
  comments,
  isFirst,
  isLast,
  onMove,
}: {
  meeting: BoardMeetingWithDetails;
  section: BoardMeetingSectionWithContents;
  number: number;
  editable: boolean;
  authUserId?: string;
  comments: BoardMeetingCommentWithCreatedBy[];
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: -1 | 1) => void;
}) {
  const { locale: t } = useLocale();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(section.name);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const rename = useBoardMeetingMutation(meeting.id, (newName: string) =>
    updateBoardMeetingSection(meeting.id, section.id, { name: newName }),
  );
  const remove = useBoardMeetingMutation(meeting.id, () =>
    deleteBoardMeetingSection(meeting.id, section.id),
  );
  const addContent = useBoardMeetingMutation(
    meeting.id,
    (type: BoardMeetingContentType) =>
      createBoardMeetingContent(meeting.id, section.id, {
        type,
        statement: "",
      }),
  );
  const reorderContents = useBoardMeetingMutation(meeting.id, (ids: string[]) =>
    reorderBoardMeetingContents(meeting.id, section.id, ids),
  );

  const contentIds = section.contents.map((content) => content.id);

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-2 border-b border-gray-300 pb-1">
        {renaming ? (
          <form
            className="flex flex-1 flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = name.trim();
              if (trimmed === "") {
                return;
              }
              void rename.mutateAsync(trimmed).then(() => setRenaming(false));
            }}
          >
            <Input
              className="flex-1 min-w-48"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button loading={rename.isPending} size="sm" type="submit">
              {t.page.save}
            </Button>
            <Button
              severity="secondary"
              size="sm"
              onClick={() => {
                setName(section.name);
                setRenaming(false);
              }}
            >
              {t.cancel}
            </Button>
          </form>
        ) : (
          <h3 className="flex-1 text-lg font-semibold m-0">
            {number}. {section.name}
          </h3>
        )}
        {editable && !renaming && (
          <div className="flex gap-1 text-gray-600">
            <IconButton
              icon={faPen}
              title={t.boardMeeting.sectionRename}
              onClick={() => setRenaming(true)}
            />
            <IconButton
              disabled={isFirst}
              icon={faArrowUp}
              title={t.boardMeeting.moveUp}
              onClick={() => onMove(-1)}
            />
            <IconButton
              disabled={isLast}
              icon={faArrowDown}
              title={t.boardMeeting.moveDown}
              onClick={() => onMove(1)}
            />
            <IconButton
              icon={faTrash}
              title={t.boardMeeting.delete}
              onClick={() => setDeleteConfirmOpen(true)}
            />
          </div>
        )}
      </header>
      {section.contents.length === 0 && (
        <p className="text-sm text-gray-500 m-0">
          {t.boardMeeting.contentsEmpty}
        </p>
      )}
      {section.contents.map((content, index) => (
        <BoardMeetingContentItem
          key={content.id}
          authUserId={authUserId}
          comments={comments.filter(
            (comment) => comment.content === content.id,
          )}
          content={content}
          isFirst={index === 0}
          isLast={index === section.contents.length - 1}
          meeting={meeting}
          number={`${number}.${index + 1}`}
          onMove={(direction) =>
            reorderContents.mutate(moveId(contentIds, index, direction))
          }
        />
      ))}
      {editable && (
        <div className="flex flex-wrap gap-2">
          {Object.values(BoardMeetingContentType).map((type) => (
            <Button
              key={type}
              loading={addContent.isPending && addContent.variables === type}
              severity="secondary"
              size="sm"
              onClick={() => addContent.mutate(type)}
            >
              + {t.boardMeeting.contentTypes[type]}
            </Button>
          ))}
        </div>
      )}
      {deleteConfirmOpen && (
        <Confirm
          header={t.boardMeeting.sectionConfirmDelete}
          loading={remove.isPending}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            void (async () => {
              await remove.mutateAsync(undefined);
              setDeleteConfirmOpen(false);
            })();
          }}
        />
      )}
    </section>
  );
}
